import { makeAutoObservable } from "mobx";
import { RemoteData } from "src/common/RemoteData";
import { ZUid } from "src/types/zUid";
import { zBom2a, ZBom2a } from "../Bom2aTypes";
import { createBom2a, deleteBoms2a, loadBoms2a } from "../apiBom2a";
import { Bom2aPositionsStore } from "./Bom2aPositions";

/* eslint no-plusplus: "off" */

export const bom2aStore = makeAutoObservable({
  objectServiceId: "" as ZUid,
  setObjectServiceId(id: ZUid) {
    this.objectServiceId = id;
  },

  entityId: "" as ZUid,
  setEntityId(id: ZUid) {
    this.entityId = id;
  },

  modelObjectId: "" as ZUid,
  setModelObjectId(id: ZUid) {
    this.modelObjectId = id;
  },

  data: { status: "none" } as RemoteData<ZBom2a[]>,
  setData(rd: RemoteData<ZBom2a[]>) {
    this.data = rd;
  },
  get boms(): ZBom2a[] {
    return this.data.status === "ready" ? this.data.result : [];
  },

  curBom: null as ZBom2a | null,
  setCurBom(bom: ZBom2a | null) {
    this.curBom = bom;
  },

  async init(objectServiceId: ZUid, entityId: ZUid, modelObjectId: ZUid) {
    this.setObjectServiceId(objectServiceId);
    this.setEntityId(entityId);
    this.setModelObjectId(modelObjectId);
    this.setCurBom(null);
    this.setSelectedBomIds([]);
    await this.reload();
  },

  async reload() {
    try {
      this.setData({ status: "wait" });
      const { objectServiceId, entityId } = this;
      const boms = await loadBoms2a({ objectServiceId, entityId });
      this.setData({ status: "ready", result: boms });
    } catch (error) {
      this.setData({ status: "error", error });
    }
  },

  get tabs(): Tab[] {
    if (this.data.status !== "ready") return [];
    const boms = this.data.result;
    return [
      { key: "", label: "Summary" },
      ...boms.map(({ id, name }) => ({ key: String(id), label: name })),
    ];
  },
  get curTabKey(): string {
    const { curBom } = this;
    return curBom ? String(curBom.id) : "";
  },
  setCurTab(activeKey: string) {
    const bom = this.boms.find(({ id }) => id === activeKey);
    this.setCurBom(bom ?? null);
  },

  mode: null as Bom2aMode,
  setMode(m: Bom2aMode) {
    this.mode = m;
  },

  lockCounter: 0,
  lock() {
    this.lockCounter++;
  },
  unlock() {
    this.lockCounter--;
  },
  get buzy(): boolean {
    return this.lockCounter !== 0;
  },

  // summary
  selectedBomIds: [] as ZUid[],
  setSelectedBomIds(ids: ZUid[]) {
    this.selectedBomIds = ids;
  },
  toggleSelectedBomId(id: ZUid) {
    const selectedBomIds = [...this.selectedBomIds];
    const pos = selectedBomIds.indexOf(id);
    if (pos < 0) {
      selectedBomIds.push(id);
    } else {
      selectedBomIds.splice(pos, 1);
    }
    this.setSelectedBomIds(selectedBomIds);
  },

  beginCreate() {
    this.setMode("create");
  },
  async doCreate(values: unknown) {
    try {
      this.lock();
      const { name } = zBom2a.omit({ id: true }).parse(values);
      const { objectServiceId, entityId } = this;
      await createBom2a({ name, objectServiceId, entityId });
      this.setMode(null);
    } finally {
      this.unlock();
    }
  },

  get canDelete(): boolean {
    return this.selectedBomIds.length > 0;
  },

  async doDelete() {
    try {
      this.lock();
      const { selectedBomIds, objectServiceId } = this;
      await deleteBoms2a(objectServiceId, selectedBomIds);
      this.setSelectedBomIds([]);
      this.setMode(null);
    } finally {
      this.unlock();
    }
  },

  editorStore: new Bom2aPositionsStore(),
});

type Tab = {
  key: string;
  label: string;
};

type Bom2aMode = "create" | "delete" | null;
